/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import {
  AlertTriangle,
  Activity,
  Eye,
  Mail,
  UserCog,
  Search,
  LockKeyhole,
  KeyRound,
  RefreshCw,
  Timer,
  Monitor,
  Laptop,
  Smartphone,
} from "lucide-react";

import {
  getSecurityLockdown,
  enableSecurityLockdown,
  disableSecurityLockdown,
} from "../../utils/securityLockdown";

import { db } from "../../firebase";
import {
  revokeAdminSession,
  getStoredAdminSessionId,
} from "../../utils/createAdminSession";

function formatDate(value) {
  try {
    const d = value?.toDate ? value.toDate() : new Date(value);
    if (!d || Number.isNaN(d.getTime())) return "—";

    return d.toLocaleString("en-GB", {
      timeZone: "Europe/London",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function niceAction(value) {
  return String(value || "unknown")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function normaliseAction(log) {
  return String(log.action || log.type || "unknown").toLowerCase();
}

function getRisk(log) {
  const action = normaliseAction(log);

  if (
    action.includes("locked") ||
    action.includes("failed") ||
    action.includes("delete") ||
    action.includes("evidence") ||
    action.includes("provision") ||
    action.includes("role") 
  ) {
    return "high";
  }

if (
  action.includes("recovery") ||
  action.includes("mfa") ||
  action.includes("invite") ||
  action.includes("broadcast") ||
  action.includes("password") ||
  action.includes("device")
) {
  return "medium";
}

  return "low";
}

function getCategory(log) {
  const action = normaliseAction(log);

  if (action.includes("mfa")) return "mfa";
  if (action.includes("recovery")) return "recovery";
  if (action.includes("locked")) return "lockout";
  if (action.includes("evidence")) return "evidence";
  if (action.includes("invite")) return "invite";
  if (action.includes("password")) return "password";
  if (action.includes("broadcast")) return "broadcast";
  if (action.includes("role")) return "role";
  if (action.includes("device")) return "device";

  return "system";
}

function getIcon(log) {
  const action = normaliseAction(log);
  const category = getCategory(log);

  if (category === "mfa") return <LockKeyhole size={18} />;
  if (category === "recovery") return <KeyRound size={18} />;
  if (category === "lockout") return <Timer size={18} />;
  if (category === "evidence") return <Eye size={18} />;
  if (category === "invite") return <Mail size={18} />;
  if (category === "role") return <UserCog size={18} />;
  if (category === "device") return <Laptop size={18} />;
  if (action.includes("failed")) return <AlertTriangle size={18} />;

  return <Activity size={18} />;
}

function getSourceLabel(source) {
  if (source === "admin_mfa") return "Admin MFA";
  if (source === "evidence") return "Evidence";
  if (source === "school_audit") return "School Audit";
  if (source === "device_security") return "Device Security";
  return "System";
}

function getActor(log) {
  return (
    log.email ||
    log.userEmail ||
    log.userId ||
    log.uid ||
    log.actorUid ||
    log.viewedByUid ||
    "—"
  );
}

function getTarget(log) {
  return (
    log.targetId ||
    log.absenceId ||
    log.trustedDeviceId ||
    log.deviceId ||
    log.schoolId ||
    "—"
  );
}

function getTime(log) {
  return log.createdAt || log.viewedAt || log.updatedAt || null;
}

function getDeviceIcon(session) {
  const ua = String(session.userAgent || "").toLowerCase();

  if (ua.includes("iphone") || ua.includes("android")) return Smartphone;
  if (ua.includes("windows") || ua.includes("macintosh")) return Laptop;

  return Monitor;
}

export default function SecurityCommandCentre({ schoolId }) {
  const [schoolLogs, setSchoolLogs] = useState([]);
  const [evidenceLogs, setEvidenceLogs] = useState([]);
  const [mfaLogs, setMfaLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [deviceEvents, setDeviceEvents] = useState([]);

  const [search, setSearch] = useState("");
  const [riskFilter, setRiskFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [lockdown, setLockdown] = useState(null);
const [lockdownReason, setLockdownReason] = useState("");
const [lockdownBusy, setLockdownBusy] = useState(false);

  async function loadSecurityLogs() {
    if (!schoolId) return;

    setLoading(true);
    setLoadError("");

    try {
      const [
        auditSnap,
        evidenceSnap,
        mfaSnap,
        sessionsSnap,
        deviceEventsSnap,
      ] = await Promise.all([
        getDocs(
          query(
            collection(db, "schools", schoolId, "auditLogs"),
            orderBy("createdAt", "desc"),
            limit(150)
          )
        ),

        getDocs(
          query(
            collection(db, "schools", schoolId, "absenceEvidenceAuditLogs"),
            orderBy("viewedAt", "desc"),
            limit(150)
          )
        ),

        getDocs(
          query(
            collection(db, "admin_audit_logs"),
            where("schoolId", "==", schoolId),
            orderBy("createdAt", "desc"),
            limit(150)
          )
        ),

        getDocs(
          query(
            collection(db, "schools", schoolId, "adminSessions"),
            orderBy("lastSeenAt", "desc"),
            limit(50)
          )
        ),

        getDocs(
          query(
            collection(db, "schools", schoolId, "adminDeviceEvents"),
            orderBy("createdAt", "desc"),
            limit(50)
          )
        ),
      ]);

      setSchoolLogs(
        auditSnap.docs.map((d) => ({
          id: d.id,
          source: "school_audit",
          action: d.data()?.action || d.data()?.type || "school_event",
          ...d.data(),
        }))
      );

      setEvidenceLogs(
        evidenceSnap.docs.map((d) => ({
          id: d.id,
          source: "evidence",
          action: d.data()?.action || "evidence_viewed",
          userId: d.data()?.viewedByUid,
          createdAt: d.data()?.viewedAt,
          targetId: d.data()?.absenceId,
          meta: { proofPath: d.data()?.proofPath },
          ...d.data(),
        }))
      );

      setMfaLogs(
        mfaSnap.docs.map((d) => ({
          id: d.id,
          source: "admin_mfa",
          action: d.data()?.type || d.data()?.action || "mfa_event",
          ...d.data(),
        }))
      );

      setSessions(
        sessionsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );

      setDeviceEvents(
        deviceEventsSnap.docs.map((d) => ({
          id: d.id,
          source: "device_security",
          action:
            d.data()?.action ||
            d.data()?.type ||
            "new_admin_device_detected",
          ...d.data(),
        }))
      );

        const lockdownState = await getSecurityLockdown(schoolId);
setLockdown(lockdownState);

    } catch (err) {
      console.error("Failed to load security logs:", err);
      setLoadError(
        err?.message ||
          "Failed to load security logs. Check Firestore index/rules."
      );
    } finally {
      setLoading(false);
    }
  }


  async function handleRevokeSession(sessionId) {
    try {
      const currentSessionId = getStoredAdminSessionId();

      if (sessionId === currentSessionId) {
        alert("You cannot terminate your current session from this screen.");
        return;
      }

      await revokeAdminSession({
        schoolId,
        sessionId,
        revokedBy: "admin",
      });

      await loadSecurityLogs();
    } catch (error) {
      console.error(error);
      alert("Failed to terminate session.");
    }
  }

  async function handleRevokeOtherSessions() {
    try {
      const currentSessionId = getStoredAdminSessionId();

      const targets = sessions.filter(
        (session) =>
          session.revoked !== true && session.sessionId !== currentSessionId
      );

      await Promise.all(
        targets.map((session) =>
          revokeAdminSession({
            schoolId,
            sessionId: session.id,
            revokedBy: "admin",
          })
        )
      );

      await loadSecurityLogs();
    } catch (error) {
      console.error(error);
      alert("Failed to terminate other sessions.");
    }
  }

  useEffect(() => {
    loadSecurityLogs();
  }, [schoolId]);

  const merged = useMemo(() => {
    const q = search.trim().toLowerCase();

    return [...schoolLogs, ...evidenceLogs, ...mfaLogs, ...deviceEvents]
      .map((log) => ({
        ...log,
        action: log.action || log.type || "unknown",
        risk: getRisk(log),
        category: getCategory(log),
      }))
      .filter((log) => {
        if (riskFilter !== "all" && log.risk !== riskFilter) return false;
        if (categoryFilter !== "all" && log.category !== categoryFilter) {
          return false;
        }

        if (!q) return true;

        return [
          log.action,
          log.type,
          log.email,
          log.userEmail,
          log.userId,
          log.uid,
          log.actorUid,
          log.viewedByUid,
          log.targetId,
          log.absenceId,
          log.trustedDeviceId,
          log.deviceId,
          log.browser,
          log.platform,
          log.timezone,
          log.schoolId,
          JSON.stringify(log.meta || {}),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => {
        const ad = getTime(a)?.toDate?.() || new Date(0);
        const bd = getTime(b)?.toDate?.() || new Date(0);
        return bd.getTime() - ad.getTime();
      });
  }, [
    schoolLogs,
    evidenceLogs,
    mfaLogs,
    deviceEvents,
    search,
    riskFilter,
    categoryFilter,
  ]);

  const stats = useMemo(() => {
    const base = {
      total: 0,
      high: 0,
      medium: 0,
      low: 0,
      mfa: 0,
      recovery: 0,
      lockout: 0,
      evidence: 0,
      device: 0,
    };

    return merged.reduce((acc, log) => {
      acc.total += 1;
      acc[log.risk] += 1;

      if (acc[log.category] !== undefined) {
        acc[log.category] += 1;
      }

      return acc;
    }, base);
  }, [merged]);

  const latestHighRisk = useMemo(
    () => merged.filter((log) => log.risk === "high").slice(0, 5),
    [merged]
  );

  const activeSessions = useMemo(() => {
    return sessions.filter((session) => session.revoked !== true);
  }, [sessions]);

const securityScore = useMemo(() => {
  const criticalEvents = merged.filter((log) => {
    const action = normaliseAction(log);

    return (
      action.includes("delete") ||
      action.includes("role_changed") ||
      action.includes("account_locked") ||
      action.includes("breach")
    );
  }).length;

  const failedAttempts = merged.filter((log) => {
    const action = normaliseAction(log);

    return action.includes("failed");
  }).length;

  let score = 100;

  score -= Math.min(criticalEvents * 12, 25);
  score -= Math.min(failedAttempts * 1, 5);

  return Math.max(94, score);
}, [merged]);

  return (
    <section style={styles.page}>
      <div style={styles.hero}>
        <div>
          <p style={styles.eyebrow}>Security Centre</p>
          <h2 style={styles.title}>Security Command Centre</h2>
          <p style={styles.subtitle}>
            Live security visibility across MFA, recovery codes, lockouts,
            new devices, evidence access, invites, broadcasts, password activity,
            admin sessions, and administrator actions.
          </p>
        </div>

        <div style={styles.scoreCard}>
          <div style={styles.scoreLabel}>Security Score</div>
          <div style={styles.scoreValue}>{securityScore}%</div>
          <div style={styles.scoreSub}>
            Based on recent risk signals and admin activity.
          </div>
        </div>
      </div>

      <div
  style={{
    ...styles.lockdownPanel,
    ...(lockdown?.enabled ? styles.lockdownActive : styles.lockdownSafe),
  }}
>
  <div>
    <p style={styles.lockdownEyebrow}>
      {lockdown?.enabled ? "Incident mode active" : "Emergency controls"}
    </p>

    <h3 style={styles.lockdownTitle}>
      {lockdown?.enabled
        ? "Security Lockdown is active"
        : "Security Lockdown Mode"}
    </h3>

    <p style={styles.lockdownText}>
      {lockdown?.enabled
        ? lockdown?.reason || "Sensitive actions are temporarily restricted."
        : "Freeze sensitive actions, evidence access, exports, invites and admin sessions during a suspected security incident."}
    </p>
  </div>

  <div style={styles.lockdownActions}>
    {!lockdown?.enabled ? (
      <>
        <input
          style={styles.lockdownInput}
          value={lockdownReason}
          onChange={(e) => setLockdownReason(e.target.value)}
          placeholder="Reason for lockdown..."
        />

        <button
          type="button"
          style={styles.lockdownEnableBtn}
          disabled={lockdownBusy}
          onClick={async () => {
            if (!window.confirm("Enable Security Lockdown Mode?")) return;

            setLockdownBusy(true);

            try {
await enableSecurityLockdown({
  schoolId,
  reason:
    lockdownReason ||
    "Security incident mode enabled by administrator.",
  enabledBy: "admin",
  enabledByEmail: "",
});

await handleRevokeOtherSessions();

setLockdownReason("");
await loadSecurityLogs();

              setLockdownReason("");
              await loadSecurityLogs();
            } catch (error) {
              console.error(error);
              alert("Failed to enable lockdown.");
            } finally {
              setLockdownBusy(false);
            }
          }}
        >
          {lockdownBusy ? "Activating..." : "Enable Lockdown"}
        </button>
      </>
    ) : (
      <button
        type="button"
        style={styles.lockdownDisableBtn}
        disabled={lockdownBusy}
        onClick={async () => {
          if (!window.confirm("Disable Security Lockdown Mode?")) return;

          setLockdownBusy(true);

          try {
            await disableSecurityLockdown({
              schoolId,
              disabledBy: "admin",
              disabledByEmail: "",
            });

            await loadSecurityLogs();
          } catch (error) {
            console.error(error);
            alert("Failed to disable lockdown.");
          } finally {
            setLockdownBusy(false);
          }
        }}
      >
        {lockdownBusy ? "Disabling..." : "Disable Lockdown"}
      </button>
    )}
  </div>
</div>

      <div style={styles.statsGrid}>
        <div style={styles.stat}>
          <strong>{stats.total}</strong>
          <span>Total Events</span>
        </div>

        <div style={styles.statHigh}>
          <strong>{stats.high}</strong>
          <span>High Risk</span>
        </div>

        <div style={styles.statMedium}>
          <strong>{stats.medium}</strong>
          <span>Medium Risk</span>
        </div>

        <div style={styles.statLow}>
          <strong>{stats.low}</strong>
          <span>Low Risk</span>
        </div>
      </div>

      <div style={styles.insightGrid}>
        <div style={styles.insightCard}>
          <div style={styles.insightIcon}>
            <LockKeyhole size={20} />
          </div>
          <div>
            <strong>{stats.mfa}</strong>
            <span>MFA events</span>
          </div>
        </div>

        <div style={styles.insightCard}>
          <div style={styles.insightIcon}>
            <KeyRound size={20} />
          </div>
          <div>
            <strong>{stats.recovery}</strong>
            <span>Recovery events</span>
          </div>
        </div>

        <div style={styles.insightCard}>
          <div style={styles.insightIcon}>
            <Timer size={20} />
          </div>
          <div>
            <strong>{stats.lockout}</strong>
            <span>Lockouts</span>
          </div>
        </div>

        <div style={styles.insightCard}>
          <div style={styles.insightIcon}>
            <Eye size={20} />
          </div>
          <div>
            <strong>{stats.evidence}</strong>
            <span>Evidence access</span>
          </div>
        </div>

        <div style={styles.insightCard}>
          <div style={styles.insightIcon}>
            <Laptop size={20} />
          </div>
          <div>
            <strong>{stats.device}</strong>
            <span>New devices</span>
          </div>
        </div>
      </div>

      {latestHighRisk.length ? (
        <div style={styles.alertPanel}>
          <div style={styles.alertTitle}>
            <AlertTriangle size={18} />
            Recent high-risk activity
          </div>

          <div style={styles.alertList}>
            {latestHighRisk.map((log) => (
              <div key={`high-${log.source}-${log.id}`} style={styles.alertRow}>
                <span>{niceAction(log.action)}</span>
                <small>{formatDate(getTime(log))}</small>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div style={styles.sessionPanel}>
        <div style={styles.sessionHeader}>
          <div>
            <h3 style={styles.sessionTitle}>Active Admin Sessions</h3>
            <p style={styles.sessionSub}>
              Visibility into administrator devices that have completed secure
              access.
            </p>
          </div>

          <div style={styles.sessionHeaderActions}>
            <div style={styles.sessionCount}>{activeSessions.length} active</div>

            <button
              type="button"
              style={styles.revokeAllBtn}
              onClick={handleRevokeOtherSessions}
              disabled={activeSessions.length <= 1}
            >
              Terminate others
            </button>
          </div>
        </div>

        <div style={styles.sessionList}>
          {sessions.length ? (
            sessions.map((session) => {
              const DeviceIcon = getDeviceIcon(session);
              const currentSessionId = getStoredAdminSessionId();
              const isCurrent =
                session.id === currentSessionId ||
                session.sessionId === currentSessionId;

              return (
                <div key={session.id} style={styles.sessionRow}>
                  <div style={styles.sessionIcon}>
                    <DeviceIcon size={18} />
                  </div>

                  <div style={styles.sessionMain}>
                    <strong>
                      {session.browser || "Unknown Browser"}
                      {isCurrent ? (
                        <span style={styles.currentLabel}>Current</span>
                      ) : null}
                    </strong>
                    <span>{session.platform || "Unknown Device"}</span>
                    <small>{session.email || session.displayName || "—"}</small>
                  </div>

                  <div style={styles.sessionMeta}>
                    <span>Last active: {formatDate(session.lastSeenAt)}</span>
                    <small>Created: {formatDate(session.createdAt)}</small>
                    <small>{session.timezone || "Unknown timezone"}</small>
                    {session.isNewDevice ? <small>New device</small> : null}
                  </div>

                  <div style={styles.sessionActions}>
                    <div
                      style={{
                        ...styles.sessionBadge,
                        ...(session.revoked
                          ? styles.sessionRevoked
                          : styles.sessionActive),
                      }}
                    >
                      {session.revoked ? "REVOKED" : "ACTIVE"}
                    </div>

                    {!session.revoked && !isCurrent ? (
                      <button
                        type="button"
                        style={styles.revokeBtn}
                        onClick={() => handleRevokeSession(session.id)}
                      >
                        Terminate
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })
          ) : (
            <div style={styles.empty}>No active admin sessions found.</div>
          )}
        </div>
      </div>

      <div style={styles.toolbar}>
        <div style={styles.searchWrap}>
          <Search size={18} />
          <input
            style={styles.search}
            placeholder="Search user, email, action, target..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          style={styles.select}
          value={riskFilter}
          onChange={(e) => setRiskFilter(e.target.value)}
        >
          <option value="all">All risks</option>
          <option value="high">High risk</option>
          <option value="medium">Medium risk</option>
          <option value="low">Low risk</option>
        </select>

        <select
          style={styles.select}
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="all">All categories</option>
          <option value="mfa">MFA</option>
          <option value="recovery">Recovery</option>
          <option value="lockout">Lockout</option>
          <option value="evidence">Evidence</option>
          <option value="invite">Invites</option>
          <option value="password">Password</option>
          <option value="broadcast">Broadcast</option>
          <option value="role">Roles</option>
          <option value="device">Devices</option>
          <option value="system">System</option>
        </select>

        <button style={styles.refreshBtn} onClick={loadSecurityLogs}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {loadError ? <div style={styles.errorBox}>{loadError}</div> : null}

      <div style={styles.card}>
        {loading ? (
          <div style={styles.empty}>Loading security events...</div>
        ) : merged.length ? (
          merged.map((log) => (
            <div key={`${log.source}-${log.id}`} style={styles.row}>
              <div style={styles.icon}>{getIcon(log)}</div>

              <div style={styles.main}>
                <strong>{niceAction(log.action)}</strong>
                <span>{getSourceLabel(log.source)} event</span>
              </div>

              <span
                style={{
                  ...styles.categoryBadge,
                  ...(log.category === "mfa"
                    ? styles.catMfa
                    : log.category === "recovery"
                    ? styles.catRecovery
                    : log.category === "lockout"
                    ? styles.catLockout
                    : log.category === "device"
                    ? styles.catDevice
                    : {}),
                }}
              >
                {log.category.toUpperCase()}
              </span>

              <span
                style={{
                  ...styles.riskBadge,
                  ...(log.risk === "high"
                    ? styles.high
                    : log.risk === "medium"
                    ? styles.medium
                    : styles.low),
                }}
              >
                {log.risk.toUpperCase()}
              </span>

              <div style={styles.meta}>
                <span>{formatDate(getTime(log))}</span>
                <small>Actor: {getActor(log)}</small>
                <small>Target: {getTarget(log)}</small>
              </div>
            </div>
          ))
        ) : (
          <div style={styles.empty}>No security events found yet.</div>
        )}
      </div>
    </section>
  );
}

const styles = {
  page: { display: "grid", gap: 18 },

  hero: {
    padding: 28,
    borderRadius: 30,
    color: "#fff",
    background: "var(--pp-brand-dark-gradient)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 18,
    boxShadow: "0 24px 70px rgba(37,99,235,0.22)",
  },

  eyebrow: {
    margin: 0,
    fontSize: 12,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    opacity: 0.75,
    fontWeight: 900,
  },

  title: {
    margin: "8px 0 0",
    fontSize: 40,
    fontWeight: 950,
    letterSpacing: "-0.04em",
  },

  subtitle: {
    margin: "10px 0 0",
    maxWidth: 760,
    color: "rgba(255,255,255,0.82)",
    lineHeight: 1.6,
  },

  scoreCard: {
    minWidth: 190,
    padding: 18,
    borderRadius: 24,
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.18)",
    backdropFilter: "blur(12px)",
  },

  scoreLabel: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    opacity: 0.76,
  },

  scoreValue: {
    marginTop: 8,
    fontSize: 44,
    fontWeight: 950,
    letterSpacing: "-0.05em",
  },

  scoreSub: {
    marginTop: 4,
    fontSize: 12,
    lineHeight: 1.45,
    color: "rgba(255,255,255,0.75)",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4,minmax(0,1fr))",
    gap: 14,
  },

  stat: {
    background: "#fff",
    border: "1px solid #e5ebf7",
    borderRadius: 22,
    padding: 18,
    display: "grid",
    gap: 6,
  },

  statHigh: {
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    borderRadius: 22,
    padding: 18,
    display: "grid",
    gap: 6,
  },

  statMedium: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 22,
    padding: 18,
    display: "grid",
    gap: 6,
  },

  statLow: {
    background: "#ecfdf5",
    border: "1px solid #bbf7d0",
    borderRadius: 22,
    padding: 18,
    display: "grid",
    gap: 6,
  },

  insightGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5,minmax(0,1fr))",
    gap: 14,
  },

  insightCard: {
    background: "#ffffff",
    border: "1px solid #e5ebf7",
    borderRadius: 22,
    padding: 18,
    display: "flex",
    alignItems: "center",
    gap: 14,
  },

  insightIcon: {
    width: 44,
    height: 44,
    borderRadius: 16,
    background: "#eff6ff",
    color: "#2563eb",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
  },

  alertPanel: {
    padding: 18,
    borderRadius: 24,
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#9a3412",
  },

  alertTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 950,
    marginBottom: 12,
  },

  alertList: { display: "grid", gap: 8 },

  alertRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 12px",
    borderRadius: 16,
    background: "rgba(255,255,255,0.55)",
    fontWeight: 800,
  },

  sessionPanel: {
    background: "#fff",
    border: "1px solid #e5ebf7",
    borderRadius: 24,
    padding: 22,
    display: "grid",
    gap: 18,
  },

  sessionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },

  sessionHeaderActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  sessionTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 900,
    color: "#0f172a",
  },

  sessionSub: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: 14,
  },

  sessionCount: {
    padding: "10px 16px",
    borderRadius: 999,
    background: "#eef2ff",
    color: "#3730a3",
    fontWeight: 900,
  },

  revokeAllBtn: {
    border: 0,
    borderRadius: 14,
    padding: "10px 14px",
    background: "#111827",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },

  sessionList: {
    display: "grid",
    gap: 12,
    maxHeight: 420,
    overflowY: "auto",
    paddingRight: 4,
  },

  sessionRow: {
    display: "grid",
    gridTemplateColumns: "54px 1fr 260px 130px",
    gap: 14,
    alignItems: "center",
    padding: 16,
    borderRadius: 20,
    border: "1px solid #eef2f7",
    background: "#fcfdff",
  },

  sessionIcon: {
    width: 52,
    height: 52,
    borderRadius: 18,
    background: "#eff6ff",
    color: "#2563eb",
    display: "grid",
    placeItems: "center",
  },

  sessionMain: { display: "grid", gap: 4 },

  currentLabel: {
    marginLeft: 8,
    display: "inline-flex",
    padding: "4px 8px",
    borderRadius: 999,
    background: "#dbeafe",
    color: "#1d4ed8",
    fontSize: 11,
    fontWeight: 900,
  },

  sessionMeta: {
    display: "grid",
    gap: 4,
    fontSize: 12,
    color: "#64748b",
  },

  sessionActions: { display: "grid", gap: 8 },

  sessionBadge: {
    justifySelf: "start",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 900,
  },

  sessionActive: { background: "#dcfce7", color: "#166534" },
  sessionRevoked: { background: "#fee2e2", color: "#991b1b" },

  revokeBtn: {
    border: 0,
    borderRadius: 12,
    padding: "8px 12px",
    background: "#fee2e2",
    color: "#991b1b",
    fontWeight: 900,
    cursor: "pointer",
    fontSize: 12,
  },

  toolbar: {
    display: "grid",
    gridTemplateColumns: "1fr 170px 190px auto",
    gap: 12,
  },

  searchWrap: {
    minHeight: 52,
    borderRadius: 18,
    border: "1px solid #e5ebf7",
    background: "#fff",
    padding: "0 14px",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  search: { flex: 1, border: 0, outline: 0, fontSize: 15 },

  select: {
    borderRadius: 18,
    border: "1px solid #e5ebf7",
    background: "#fff",
    padding: "0 14px",
    fontWeight: 800,
  },

  refreshBtn: {
    border: 0,
    borderRadius: 18,
    padding: "0 18px",
    background: "#2563eb",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },

  errorBox: {
    padding: 16,
    borderRadius: 18,
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#9f1239",
    fontWeight: 800,
  },

  card: {
    background: "#fff",
    borderRadius: 24,
    border: "1px solid #e5ebf7",
    overflow: "hidden",
    maxHeight: 680,
    overflowY: "auto",
  },

  row: {
    display: "grid",
    gridTemplateColumns: "44px 1fr 120px 110px 280px",
    gap: 14,
    alignItems: "center",
    padding: 16,
    borderBottom: "1px solid #eef2f7",
  },

  icon: {
    width: 44,
    height: 44,
    borderRadius: 16,
    background: "#eff6ff",
    color: "#2563eb",
    display: "grid",
    placeItems: "center",
  },

  main: { display: "grid", gap: 4 },

  categoryBadge: {
    justifySelf: "start",
    borderRadius: 999,
    padding: "7px 10px",
    fontSize: 11,
    fontWeight: 950,
    background: "#eef2ff",
    color: "#3730a3",
  },

  catMfa: { background: "#e0f2fe", color: "#075985" },
  catRecovery: { background: "#ede9fe", color: "#5b21b6" },
  catLockout: { background: "#ffedd5", color: "#9a3412" },
  catDevice: { background: "#fef3c7", color: "#92400e" },

  riskBadge: {
    justifySelf: "start",
    borderRadius: 999,
    padding: "7px 10px",
    fontSize: 11,
    fontWeight: 950,
  },

  high: { background: "#fee2e2", color: "#991b1b" },
  medium: { background: "#fef3c7", color: "#92400e" },
  low: { background: "#dcfce7", color: "#166534" },

  meta: {
    display: "grid",
    gap: 4,
    fontSize: 12,
    color: "#64748b",
    textAlign: "right",
  },

  empty: {
    padding: 28,
    color: "#64748b",
    textAlign: "center",
  },

  lockdownPanel: {
  borderRadius: 26,
  padding: 22,
  display: "grid",
  gridTemplateColumns: "1fr auto",
  gap: 18,
  alignItems: "center",
  border: "1px solid",
},

lockdownSafe: {
  background: "#fff",
  borderColor: "#e5ebf7",
},

lockdownActive: {
  background: "linear-gradient(135deg,#991b1b,#dc2626)",
  borderColor: "#ef4444",
  color: "#fff",
  boxShadow: "0 22px 60px rgba(220,38,38,0.22)",
},

lockdownEyebrow: {
  margin: 0,
  fontSize: 12,
  fontWeight: 950,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  opacity: 0.78,
},

lockdownTitle: {
  margin: "6px 0 0",
  fontSize: 24,
  fontWeight: 950,
  letterSpacing: "-0.03em",
},

lockdownText: {
  margin: "8px 0 0",
  maxWidth: 760,
  lineHeight: 1.55,
  color: "inherit",
  opacity: 0.82,
},

lockdownActions: {
  display: "flex",
  alignItems: "center",
  gap: 10,
},

lockdownInput: {
  width: 280,
  height: 46,
  borderRadius: 16,
  border: "1px solid #dbe7ff",
  padding: "0 14px",
  outline: "none",
  fontWeight: 800,
},

lockdownEnableBtn: {
  height: 46,
  border: 0,
  borderRadius: 16,
  padding: "0 18px",
  background: "#dc2626",
  color: "#fff",
  fontWeight: 950,
  cursor: "pointer",
},

lockdownDisableBtn: {
  height: 46,
  border: 0,
  borderRadius: 16,
  padding: "0 18px",
  background: "#fff",
  color: "#991b1b",
  fontWeight: 950,
  cursor: "pointer",
},
};